<template>
  <VListItem
    :ref="`contextMenuItem-${index}`"
    class="mb-0"
    :class="{ 'mt-1': index > 0 }"
    :disabled="unref(item.disabled)"
    :link="!!item.action || !!item.items?.length"
    :data-testid="`buttonContextMenuItem-${index}`"
    :data-id="item.id"
    @click="!item.items?.length && item.action?.(context)"
  >
    <template
      v-if="item.icon"
      #prepend
    >
      <VIcon
        :ref="`contextMenuItem-${index}-icon`"
        :color="unref(item.disabled) ? '#898A8B' : item.iconColor ?? '#898A8B'"
        :icon="item.icon"
      />
    </template>
    <span
      :ref="`contextMenuItem-${index}-label`"
      :class="['text-body-2', 'font-weight-semibold', item.labelColor ? `text-${item.labelColor}` : '']"
    >
      {{ item.label }}
    </span>
    <template
      v-if="item.items?.length || item.appendedText"
      #append
    >
      <VIcon
        v-if="item.items?.length"
        :ref="`contextMenuItem-${index}-chevron`"
        icon="chevron_right"
      />
      <span
        v-else
        :ref="`contextMenuItem-${index}-appendedText`"
        class="text-subtitle-1 text-medium-emphasis"
      >
        {{ item.appendedText }}
      </span>
    </template>
    <VMenu
      v-if="item.items?.length"
      :ref="`contextMenuItem-${index}-subMenu`"
      location="end"
      activator="parent"
    >
      <VMenuList>
        <template
          v-for="(subItem, subIndex) in visibleContextMenuItems"
          :key="subIndex"
        >
          <VListItem
            :ref="`contextMenuItem-${index}-subItem-${subIndex}`"
            class="mb-0"
            :class="{ 'mt-1': index > 0 }"
            :disabled="unref(subItem.disabled)"
            :link="!!subItem.action"
            :data-id="item.id"
            @click="subItem.action?.(context)"
          >
            <template
              v-if="subItem.icon"
              #prepend
            >
              <VIcon
                :ref="`contextMenuItem-${index}-subItem-${subIndex}-icon`"
                color="#898A8B"
                :icon="subItem.icon"
              />
            </template>
            <span
              :ref="`contextMenuItem-${index}-subItem-${subIndex}-label`"
              class="text-body-2 font-weight-semibold"
            >
              {{ subItem.label }}
            </span>
            <template
              v-if="subItem.appendedText"
              #append
            >
              <span class="text-subtitle-1 text-medium-emphasis">
                {{ subItem.appendedText }}
              </span>
            </template>
          </VListItem>
        </template>
      </VMenuList>
    </VMenu>
  </VListItem>
</template>

<script lang="ts" setup generic="T">
import {
  computed,
  type MaybeRef,
  unref,
} from 'vue';

import { resolveVisibleContextMenuItems } from '../../../utils/helpers';

export type locationSide = 'top' | 'end' | 'bottom' | 'start';
export type locationAlign = 'top' | 'start' | 'center' | 'end' | 'bottom';
export type tooltipLocation
  = | locationSide
  | `${'top' | 'bottom'} ${'start' | 'end' | 'center'}`
  | `${'start' | 'end'} ${'top' | 'bottom' | 'center'}`;

export interface ContextMenuItem<T = unknown> {
  label: string;
  icon?: string;
  iconColor?: string;
  labelColor?: 'primary' | 'error' | 'success' | 'warning' | 'info';
  items?: Omit<ContextMenuItem<T>, 'items'>[];
  disabled?: MaybeRef<boolean>;
  visible?: MaybeRef<boolean>;
  action?: (context?: T) => void;
  appendedText?: string;
  id?: string;
  tooltip?: string;
  tooltipLocation?: tooltipLocation;
}

const props = defineProps<{
  item: ContextMenuItem<T>;
  index: number;
  context?: T;
}>();

const visibleContextMenuItems = computed(() => resolveVisibleContextMenuItems(props.item?.items ?? []));

</script>
